import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Check,
  Eye,
  Loader2,
  MessageSquarePlus,
  MousePointer2,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import html2canvas from 'html2canvas';
import './feedback.css';
import {
  checkFeedbackEligibility,
  submitBetaFeedback,
  type FeedbackAnnotation,
  type FeedbackCategory,
  type FeedbackSource,
  type FeedbackScreenshot,
} from '../../lib/feedbackApi';

interface BetaFeedbackWidgetProps {
  tenantId?: string | null;
  theme?: 'light' | 'dark';
  source?: FeedbackSource;
  triggerLabel?: string;
  placement?: 'floating' | 'support';
}

const CATEGORIES: { value: FeedbackCategory; label: string; placeholder: string }[] = [
  { value: 'BUG_REPORT', label: 'Report a Bug', placeholder: 'What happened, and what did you expect?' },
  { value: 'INCORRECT_DATA', label: 'Incorrect Data / Calculation', placeholder: 'Which data or calculation appears incorrect?' },
  { value: 'USABILITY_PROBLEM', label: 'Design / Usability Issue', placeholder: 'What was confusing or difficult to use?' },
  { value: 'FEATURE_SUGGESTION', label: 'Feature Request', placeholder: 'What feature would you like us to add?' },
  { value: 'PERFORMANCE_ISSUE', label: 'Performance Issue', placeholder: 'What was slow or unresponsive?' },
  { value: 'GENERAL_FEEDBACK', label: 'General Feedback', placeholder: 'Share your thoughts or suggestions...' },
];

type Point = { x: number; y: number };
type Rect = { x: number; y: number; width: number; height: number };

function makeId() {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function normalizeRect(start: Point, end: Point): Rect {
  return {
    x: Math.min(start.x, end.x),
    y: Math.min(start.y, end.y),
    width: Math.abs(end.x - start.x),
    height: Math.abs(end.y - start.y),
  };
}

export const BetaFeedbackWidget: React.FC<BetaFeedbackWidgetProps> = ({
  tenantId,
  theme = 'dark',
  source = 'BETA_TESTER',
  triggerLabel = 'Feedback',
  placement = 'floating',
}) => {
  const [isEligible, setIsEligible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState<FeedbackCategory | ''>('');
  const [categoryError, setCategoryError] = useState(false);
  const [description, setDescription] = useState('');
  const [annotations, setAnnotations] = useState<FeedbackAnnotation[]>([]);
  const [screenshots, setScreenshots] = useState<FeedbackScreenshot[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectionStart, setSelectionStart] = useState<Point | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<Point | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [panelAnchor, setPanelAnchor] = useState({ bottom: 96, right: 24 });
  const [activeScreenshotId, setActiveScreenshotId] = useState<string | null>(null);
  const [redactionMode, setRedactionMode] = useState(false);
  const [redactionStart, setRedactionStart] = useState<Point | null>(null);
  const [panelPosition, setPanelPosition] = useState<Point | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const launcherRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const redactionCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const redactionImageRef = useRef<HTMLImageElement | null>(null);
  const draggingPanelRef = useRef<{ pointerId: number; offset: Point } | null>(null);
  const submittedRef = useRef(false);
  const idempotencyKeyRef = useRef(makeId());

  const activeScreenshot = screenshots.find((shot) => shot.id === activeScreenshotId) || null;
  const activeAnnotationNumber = activeScreenshot
    ? annotations.findIndex((annotation) => annotation.screenshotId === activeScreenshot.id) + 1
    : 0;
  const selectedCategory = CATEGORIES.find((item) => item.value === category);
  const isDark = theme === 'dark';

  useEffect(() => {
    let current = true;
    checkFeedbackEligibility(tenantId, source)
      .then((eligible) => { if (current) setIsEligible(eligible); })
      .catch(() => { if (current) setIsEligible(false); });
    return () => { current = false; };
  }, [tenantId, source]);

  const cancelSelection = useCallback(() => {
    setSelectionMode(false);
    setSelectionStart(null);
    setSelectionEnd(null);
    setIsOpen(true);
  }, []);

  useEffect(() => {
    if (!selectionMode) return;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const cancelOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') cancelSelection();
    };
    window.addEventListener('keydown', cancelOnEscape);
    return () => {
      document.body.style.overflow = oldOverflow;
      window.removeEventListener('keydown', cancelOnEscape);
    };
  }, [selectionMode, cancelSelection]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!selectionMode || !canvas) return;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(window.innerWidth * ratio);
    canvas.height = Math.round(window.innerHeight * ratio);
    canvas.style.width = `${window.innerWidth}px`;
    canvas.style.height = `${window.innerHeight}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    if (selectionStart && selectionEnd) {
      const box = normalizeRect(selectionStart, selectionEnd);
      ctx.fillStyle = 'rgba(79, 70, 229, 0.20)';
      ctx.strokeStyle = '#4f46e5';
      ctx.lineWidth = 2;
      ctx.fillRect(box.x, box.y, box.width, box.height);
      ctx.strokeRect(box.x, box.y, box.width, box.height);
    }
  }, [selectionMode, selectionStart, selectionEnd]);

  const captureSelection = useCallback(async (box: Rect) => {
    if (screenshots.length >= 5) {
      setErrorMessage('You can attach up to 5 screenshots per feedback submission.');
      setSelectionMode(false);
      setIsOpen(true);
      return;
    }
    const index = Math.max(0, ...annotations.map((annotation) => annotation.index)) + 1;
    const screenshotId = makeId();
    setSelectionMode(false);
    setSelectionStart(null);
    setSelectionEnd(null);
    setIsOpen(true);
    setIsCapturing(true);
    setErrorMessage(null);

    try {
      await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const crop = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: false,
        logging: false,
        backgroundColor: '#ffffff',
        scale: dpr,
        // html2canvas expects crop coordinates in CSS pixels; `scale` handles DPR.
        x: Math.round(window.scrollX + box.x),
        y: Math.round(window.scrollY + box.y),
        width: Math.max(1, Math.round(box.width)),
        height: Math.max(1, Math.round(box.height)),
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
        ignoreElements: (element) => element.closest?.('[data-feedback-private]') !== null
          || element.id === 'feedbackRoot'
          || element.id === 'feedbackPanel'
          || element.id === 'feedbackSelectionOverlay',
        onclone: (clonedDocument) => {
          clonedDocument.querySelectorAll('[data-feedback-private], [data-sensitive], [hidden], [aria-hidden="true"], input[type="password"], input[autocomplete^="cc-"], textarea[data-private]').forEach((element) => element.remove());
          clonedDocument.querySelectorAll('input, textarea, select').forEach((element) => {
            if ('value' in element) (element as HTMLInputElement).value = '';
          });
        },
      });
      const output = document.createElement('canvas');
      output.width = Math.max(1, crop.width);
      output.height = Math.max(1, crop.height);
      const ctx = output.getContext('2d');
      if (!ctx) throw new Error('Screenshot processing is unavailable in this browser.');
      ctx.drawImage(crop, 0, 0);
      const screenshot: FeedbackScreenshot = {
        id: screenshotId,
        dataUrl: output.toDataURL('image/jpeg', 0.84),
        mimeType: 'image/jpeg',
        width: output.width,
        height: output.height,
      };
      setScreenshots((items) => [...items, screenshot]);
      setAnnotations((items) => [...items, {
        id: makeId(),
        index,
        elementSelector: 'Selected screen area',
        comment: '',
        screenshotId,
        x: Math.round(window.scrollX + box.x),
        y: Math.round(window.scrollY + box.y),
        width: Math.round(box.width),
        height: Math.round(box.height),
      }]);
    } catch (error) {
      setErrorMessage(error instanceof Error ? `Screenshot capture failed: ${error.message}` : 'Screenshot capture failed. You can still send text feedback.');
    } finally {
      setIsCapturing(false);
    }
  }, [annotations, screenshots.length]);

  const getPointerPoint = (event: React.PointerEvent<HTMLCanvasElement>): Point => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: Math.max(0, Math.min(event.clientX - rect.left, rect.width)), y: Math.max(0, Math.min(event.clientY - rect.top, rect.height)) };
  };

  const handleSelectionPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = getPointerPoint(event);
    setSelectionStart(point);
    setSelectionEnd(point);
  };

  const handleSelectionPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!selectionStart) return;
    setSelectionEnd(getPointerPoint(event));
  };

  const handleSelectionPointerUp = async (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!selectionStart) return;
    const box = normalizeRect(selectionStart, getPointerPoint(event));
    if (box.width < 24 || box.height < 24) {
      setSelectionStart(null);
      setSelectionEnd(null);
      setErrorMessage('Select an area at least 24 pixels wide and high.');
      return;
    }
    await captureSelection(box);
  };

  const updateAnnotationComment = (id: string, comment: string) => {
    setAnnotations((items) => items.map((annotation) => annotation.id === id ? { ...annotation, comment } : annotation));
  };

  const removeScreenshot = (id: string) => {
    setScreenshots((items) => items.filter((shot) => shot.id !== id));
    setAnnotations((items) => items
      .filter((annotation) => annotation.screenshotId !== id)
      .map((annotation, index) => ({ ...annotation, index: index + 1 })));
    if (activeScreenshotId === id) setActiveScreenshotId(null);
  };

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (placement === 'floating') {
      const buttonRect = event.currentTarget.getBoundingClientRect();
      setPanelAnchor({
        bottom: Math.max(8, window.innerHeight - buttonRect.top + 2),
        right: Math.max(8, document.documentElement.clientWidth - buttonRect.right),
      });
    }
    setIsOpen(true);
    setSubmitSuccess(false);
    setErrorMessage(null);
    setCategoryError(false);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setIsOpen(false);
    setActiveScreenshotId(null);
    setRedactionMode(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    const closeOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (panelRef.current?.contains(target) || launcherRef.current?.contains(target)) return;
      handleClose();
    };

    document.addEventListener('pointerdown', closeOnOutsidePointer);
    return () => document.removeEventListener('pointerdown', closeOnOutsidePointer);
  }, [isOpen, isSubmitting]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submittedRef.current || isSubmitting) return;
    if (!category) {
      setCategoryError(true);
      return;
    }
    if (description.trim().length < 5) {
      setErrorMessage('Please describe the issue or feedback in at least 5 characters.');
      return;
    }

    submittedRef.current = true;
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await submitBetaFeedback({
        tenantId,
        source,
        category,
        description: description.trim(),
        pageRoute: window.location.pathname,
        annotations,
        screenshots,
        metadata: {
          userAgent: navigator.userAgent,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
          devicePixelRatio: window.devicePixelRatio || 1,
          timestamp: new Date().toISOString(),
        },
        idempotencyKey: idempotencyKeyRef.current,
      });
      setSubmitSuccess(true);
      window.setTimeout(() => {
        setIsOpen(false);
        setDescription('');
        setCategory('');
        setCategoryError(false);
        setAnnotations([]);
        setScreenshots([]);
        setSubmitSuccess(false);
        submittedRef.current = false;
        idempotencyKeyRef.current = makeId();
      }, 1600);
    } catch (error) {
      submittedRef.current = false;
      setErrorMessage(error instanceof Error ? error.message : 'Submission failed. Your feedback is still here; try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const beginPanelDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (window.innerWidth <= 768 || (event.target as HTMLElement).closest('button')) return;
    const panel = document.getElementById('feedbackPanel');
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    draggingPanelRef.current = { pointerId: event.pointerId, offset: { x: event.clientX - rect.left, y: event.clientY - rect.top } };
    panel.setPointerCapture(event.pointerId);
  };

  const movePanel = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = draggingPanelRef.current;
    if (!drag || drag.pointerId !== event.pointerId || window.innerWidth <= 768) return;
    const panel = document.getElementById('feedbackPanel');
    const panelWidth = panel?.offsetWidth || 360;
    const panelHeight = panel?.offsetHeight || 520;
    setPanelPosition({
      x: Math.max(8, Math.min(event.clientX - drag.offset.x, window.innerWidth - panelWidth - 8)),
      y: Math.max(8, Math.min(event.clientY - drag.offset.y, window.innerHeight - panelHeight - 8)),
    });
  };

  const endPanelDrag = () => { draggingPanelRef.current = null; };

  useEffect(() => {
    if (!isOpen || !panelPosition || window.innerWidth <= 768) return;
    const keepPanelVisible = () => {
      const panel = document.getElementById('feedbackPanel');
      if (!panel) return;
      const width = panel.offsetWidth;
      const height = panel.offsetHeight;
      setPanelPosition((position) => position ? {
        x: Math.max(8, Math.min(position.x, window.innerWidth - width - 8)),
        y: Math.max(8, Math.min(position.y, window.innerHeight - height - 8)),
      } : null);
    };
    window.addEventListener('resize', keepPanelVisible);
    return () => window.removeEventListener('resize', keepPanelVisible);
  }, [isOpen, panelPosition]);

  useEffect(() => {
    if (!activeScreenshot || !redactionCanvasRef.current) return;
    const image = new Image();
    image.onload = () => {
      const canvas = redactionCanvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      ctx.drawImage(image, 0, 0);
      redactionImageRef.current = image;
    };
    image.src = activeScreenshot.dataUrl;
  }, [activeScreenshot?.dataUrl, activeScreenshotId, redactionMode]);

  const getRedactionPoint = (event: React.PointerEvent<HTMLCanvasElement>): Point => {
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    return { x: (event.clientX - rect.left) * canvas.width / rect.width, y: (event.clientY - rect.top) * canvas.height / rect.height };
  };

  const drawRedactionPreview = (end: Point) => {
    const canvas = redactionCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    const image = redactionImageRef.current;
    if (!canvas || !ctx || !image || !redactionStart) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0);
    const box = normalizeRect(redactionStart, end);
    ctx.fillStyle = 'rgba(0,0,0,0.82)';
    ctx.fillRect(box.x, box.y, box.width, box.height);
    ctx.strokeStyle = '#4f46e5';
    ctx.lineWidth = Math.max(2, canvas.width / 800);
    ctx.strokeRect(box.x, box.y, box.width, box.height);
  };

  const finishRedaction = (point: Point) => {
    const canvas = redactionCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    const image = redactionImageRef.current;
    if (!canvas || !ctx || !image || !redactionStart || !activeScreenshot) return;
    const box = normalizeRect(redactionStart, point);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0);
    if (box.width >= 4 && box.height >= 4) {
      // Burn the opaque fill into the pixel buffer; the original is replaced in component state.
      ctx.fillStyle = '#000000';
      ctx.fillRect(box.x, box.y, box.width, box.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.86);
      setScreenshots((items) => items.map((shot) => shot.id === activeScreenshot.id ? { ...shot, dataUrl, mimeType: 'image/jpeg' } : shot));
    }
    setRedactionStart(null);
  };

  if (!isEligible) return null;

  return (
    <>
      <div id="feedbackRoot" data-feedback-private className={placement === 'floating' ? 'fixed bottom-6 right-6 z-[9997] mt-[-20px] mr-[-17px] mb-[-20px]' : 'inline-flex'}>
        <button
          ref={launcherRef}
          type="button"
          onClick={handleOpen}
          aria-label={triggerLabel}
          className="feedback-launcher inline-flex items-center justify-center gap-2 rounded-t-full rounded-b-none border border-white bg-white px-4 py-2 text-sm font-medium text-[#11026A] shadow-lg transition duration-150 hover:scale-105 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          <MessageSquarePlus className="feedback-launcher-icon h-5 w-5" />
          <span>{triggerLabel}</span>
        </button>
      </div>

      {isOpen && (
        <section
          ref={panelRef}
          id="feedbackPanel"
          data-feedback-private
          role="dialog"
          aria-modal="false"
          aria-labelledby="feedbackPanelTitle"
          className={`feedback-panel-shell fixed z-[9998] flex w-[360px] max-w-[calc(100vw-48px)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl ${panelPosition ? '' : ''}`}
          style={panelPosition ? { left: panelPosition.x, top: panelPosition.y, right: 'auto', bottom: 'auto' } : { bottom: panelAnchor.bottom, right: panelAnchor.right }}
        >
          <div
            onPointerDown={beginPanelDrag}
            onPointerMove={movePanel}
            onPointerUp={endPanelDrag}
            onPointerCancel={endPanelDrag}
            className="desktop-drag-handle flex shrink-0 touch-none select-none items-center justify-between bg-slate-900 px-5 py-4 text-white"
          >
            <h2 id="feedbackPanelTitle" className="flex items-center gap-2 text-sm font-semibold"><MessageSquarePlus className="h-5 w-5" />Send Feedback</h2>
            <button type="button" onClick={handleClose} aria-label="Close feedback" className="rounded p-1 text-slate-300 hover:bg-slate-800 hover:text-white"><X className="h-5 w-5" /></button>
          </div>

          {submitSuccess ? (
            <div className="flex min-h-64 flex-col items-center justify-center bg-white p-6 text-center">
              <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-500"><Check className="h-8 w-8" /></span>
              <h3 className="text-lg font-bold text-slate-900">Feedback received</h3>
              <p className="mt-2 text-sm text-slate-500">Thank you! We’ll look into it right away.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col bg-white">
              <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
                {errorMessage && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">{errorMessage}</div>}
                <label className="block text-sm font-medium text-slate-700">
                  Issue Type
                  <select value={category} onChange={(event) => { setCategory(event.target.value as FeedbackCategory | ''); setCategoryError(false); }} className="feedback-category-select mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 pr-9 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="" disabled>Select an Issue Type</option>
                    {CATEGORIES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Description
                  <textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={3} maxLength={5000} placeholder={selectedCategory?.placeholder || 'Describe the issue or share your suggestion...'} className="feedback-description mt-1 block w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </label>
                <button type="button" onClick={() => { setErrorMessage(null); setIsOpen(false); setSelectionMode(true); }} disabled={isCapturing || screenshots.length >= 5} className="flex w-full items-center justify-center gap-2.5 rounded-2xl border border-indigo-200 bg-[#f0f4ff] px-5 py-3 text-sm font-medium tracking-tight text-indigo-700 shadow-sm transition hover:bg-[#e6edff] disabled:cursor-not-allowed disabled:opacity-50">
                  <MousePointer2 className="h-[18px] w-[18px] shrink-0" />
                  <span>{screenshots.length >= 5 ? 'Screenshot limit reached (5/5)' : screenshots.length ? `✓ ${screenshots.length} area${screenshots.length > 1 ? 's' : ''} selected — Add another` : 'Select an area to highlight'}</span>
                </button>
                {screenshots.length >= 5 && <p className="-mt-2 text-xs font-medium text-slate-700" role="status">The maximum of 5 screenshots is attached. Remove one to add another.</p>}
                <section className="feedback-attachments flex flex-col gap-2" aria-label="Screenshot attachments">
                  <div className="flex justify-between px-1 text-xs font-medium text-slate-600"><span>Attached Screenshots</span><span aria-live="polite">{screenshots.length}/5</span></div>
                  {screenshots.length > 0 && (
                    <div className="flex flex-col gap-2">
                      {screenshots.map((shot, position) => {
                        const annotation = annotations.find((item) => item.screenshotId === shot.id);
                        if (!annotation) return null;
                        const number = position + 1;
                        return (
                          <article key={shot.id} className="feedback-attachment-card flex min-w-0 flex-col gap-2 rounded-xl border border-slate-200 bg-white p-2.5">
                            <div className="flex min-w-0 items-center gap-2">
                              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">{number}</span>
                              <span className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-800">Selected screen area</span>
                              <button type="button" onClick={() => removeScreenshot(shot.id)} aria-label={`Remove screenshot ${number} and annotation`} title={`Remove screenshot ${number}`} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-600 transition hover:bg-red-50 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"><Trash2 className="h-4 w-4" /></button>
                            </div>
                            <button type="button" onClick={() => { setActiveScreenshotId(shot.id); setRedactionMode(false); }} aria-label={`Preview screenshot ${number}`} className="feedback-attachment-preview relative block w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                              <img src={shot.dataUrl} width={shot.width} height={shot.height} alt={`Screenshot ${number}, selected screen area`} className="block h-auto max-h-[180px] w-full object-contain" />
                            </button>
                            <label className="sr-only" htmlFor={`feedback-note-${annotation.id}`}>Note for screenshot {number} (optional)</label>
                            <textarea id={`feedback-note-${annotation.id}`} value={annotation.comment} onChange={(event) => updateAnnotationComment(annotation.id, event.target.value)} maxLength={1000} rows={2} placeholder="Add a note about this area (optional)..." className="feedback-attachment-note block min-h-10 w-full resize-y rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs leading-4 text-slate-800 placeholder:text-slate-600 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                          </article>
                        );
                      })}
                    </div>
                  )}
                </section>
              </div>
              <div className="flex shrink-0 items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
                {categoryError && <p role="alert" className="min-w-0 flex-1 text-[11px] leading-tight text-red-700">Select an issue type before submitting.</p>}
                <button type="submit" disabled={isSubmitting || isCapturing} className="ml-auto inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:cursor-wait disabled:opacity-60">
                  {isSubmitting || isCapturing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {isCapturing ? 'Capturing…' : isSubmitting ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            </form>
          )}
        </section>
      )}

      {selectionMode && (
        <div id="feedbackSelectionOverlay" className="fixed inset-0 z-[10000] touch-none bg-slate-900/10" data-feedback-private>
          <canvas ref={canvasRef} className="absolute inset-0 cursor-crosshair touch-none" onPointerDown={handleSelectionPointerDown} onPointerMove={handleSelectionPointerMove} onPointerUp={handleSelectionPointerUp} onPointerCancel={cancelSelection} />
          <div className="pointer-events-none absolute left-1/2 top-6 flex -translate-x-1/2 items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-lg">
            <MousePointer2 className="h-4 w-4" />Click and drag to select an area
          </div>
          <button type="button" onClick={cancelSelection} className="absolute right-4 top-4 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-lg hover:bg-slate-800">Cancel <span className="ml-1 text-slate-300">(Esc)</span></button>
        </div>
      )}

      {activeScreenshot && (
        <div className="fixed inset-0 z-[10001] flex flex-col items-center justify-center bg-black/80 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="feedbackScreenshotTitle" onClick={(event) => { if (event.target === event.currentTarget) setActiveScreenshotId(null); }}>
          <div className="flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between bg-slate-900 px-5 py-3.5 text-white">
              <h3 id="feedbackScreenshotTitle" className="flex items-center gap-2 text-sm font-semibold"><Eye className="h-4 w-4 text-indigo-400" />Screenshot Preview{activeAnnotationNumber > 0 ? ` · ${activeAnnotationNumber}` : ''}</h3>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => { removeScreenshot(activeScreenshot.id); setActiveScreenshotId(null); }} className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-slate-300 hover:text-red-300"><Trash2 className="h-4 w-4" />Delete</button>
                <button type="button" aria-label="Close screenshot preview" onClick={() => { setActiveScreenshotId(null); setRedactionMode(false); }} className="rounded p-1 text-slate-300 hover:bg-slate-800 hover:text-white"><X className="h-5 w-5" /></button>
              </div>
            </div>
            <div className="flex min-h-[240px] flex-1 items-center justify-center overflow-auto bg-slate-950/90 p-4">
              {redactionMode ? <p className="sr-only">Draw over an area to permanently black it out.</p> : null}
              <div className="relative max-h-[70vh] max-w-full">
                {redactionMode ? (
                  <canvas
                    ref={redactionCanvasRef}
                    className="block max-h-[70vh] max-w-full touch-none rounded-lg border border-slate-700 object-contain"
                    style={{ width: activeScreenshot.width, height: 'auto' }}
                    onPointerDown={(event) => { event.currentTarget.setPointerCapture(event.pointerId); setRedactionStart(getRedactionPoint(event)); }}
                    onPointerMove={(event) => { if (!redactionStart) return; drawRedactionPreview(getRedactionPoint(event)); }}
                    onPointerUp={(event) => { if (redactionStart) finishRedaction(getRedactionPoint(event)); }}
                  />
                ) : <img src={activeScreenshot.dataUrl} alt={`Screenshot ${activeAnnotationNumber}, selected screen area`} className="max-h-[70vh] max-w-full rounded-lg border border-slate-800 object-contain shadow-xl" />}
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-5 py-3 text-xs text-slate-500">
              <span>{redactionMode ? 'Drag across sensitive information; redactions are permanently applied to the image.' : 'Annotated screen capture. Images are uploaded only when you submit feedback.'}</span>
              <button type="button" onClick={() => { setActiveScreenshotId(null); setRedactionMode(false); }} className="rounded bg-slate-900 px-4 py-1.5 font-medium text-white hover:bg-slate-800">Close Preview</button>
            </div>
          </div>
        </div>
      )}
      <span className="sr-only">{isDark ? 'Dark theme' : ''}</span>
    </>
  );
};

export default BetaFeedbackWidget;
